import React, { useState, useRef, useCallback, useMemo } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, } from '@frontend/shadcn/components/ui/dialog';
import { Button } from '@frontend/shadcn/components/ui/button';
import { QuestionCircleFilled, UploadOutlined, CloseOutlined, PaperClipOutlined, ArrowLeftOutlined, ArrowRightOutlined } from '@ant-design/icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileArrowUp } from '@fortawesome/free-solid-svg-icons';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@frontend/shadcn/components/ui/accordion';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@frontend/shadcn/components/ui/select';
import { Input } from '@frontend/shadcn/components/ui/input';
import { Checkbox } from '@frontend/shadcn/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@frontend/shadcn/components/ui/tooltip';
import ShadCnAlert from '@frontend/app/refresh-components/Alert';
import { CircleInfoIcon } from '@revfluence/fresh-icons/solid/esm';
import { GET_IMPORTED_COLLECTIONS_BY_CLIENT_ID_QUERY } from '@frontend/applications/ProductFulfillmentApp/queries/importerManager';
import { useHandleCSVImportCollections } from '../ProductFulfillment/Products/hooks/useHandleCSVImportCollections';
import { useProcessRowToProduct } from './hooks/useProcessRowToProduct';
import { FieldMapping, ProductData } from './CSVTypes';

interface IProps {
    isProductUploadModalOpen: boolean;
    setIsProductUploadModalOpen: (isModalOpen: boolean) => void;
}

type ModalStep = 'upload' | 'mapping' | 'preview' | 'processing' | 'results';

export const CSVCollectionUploadModal = ({ isProductUploadModalOpen, setIsProductUploadModalOpen }: IProps) => {
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [isDragOver, setIsDragOver] = useState(false);
    const [isUploading, _setIsUploading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [csvData, setCsvData] = useState<Record<string, string>[]>([]);
    const [currentStep, setCurrentStep] = useState<ModalStep>('upload');
    const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
    const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
    // const [skipDuplicates, setSkipDuplicates] = useState(true);
    const [_validRecords, setValidRecords] = useState<ProductData[]>([]);
    const [invalidRecords, setInvalidRecords] = useState<{ row: Record<string, string>, errors: string[], rowNumber: number }[]>([]);
    const [processingProgress, setProcessingProgress] = useState(0);
    const [importResults, setImportResults] = useState<{ valid: number, invalid: number, imported: number } | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    const resetModalState = useCallback(() => {
        setSelectedFile(null);
        setCsvData([]);
        setCsvHeaders([]);
        setFieldMappings([]);
        setValidRecords([]);
        setInvalidRecords([]);
        setImportResults(null);
        setError(null);
        setCurrentStep('upload');
        setProcessingProgress(0);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleCloseModal = useCallback(() => {
        resetModalState();
        setIsProductUploadModalOpen(false);
    }, [resetModalState, setIsProductUploadModalOpen]);

    // CSV Import mutation
    const { handleCSVImportCollections, isImportingCSVCollections, csvImportError } = useHandleCSVImportCollections({
        refetchQueries: [
            { query: GET_IMPORTED_COLLECTIONS_BY_CLIENT_ID_QUERY }
        ],
        awaitRefetchQueries: true,
    });

    // CSV row processing hook
    const { processRowToProduct } = useProcessRowToProduct();

    // Required fields validation
    const requiredFields = useMemo(() => [
        'Collection ID', 'Collection Name', 'Product ID', 'Product Handle', 'Product Title', 'Variant ID', 'Variant SKU', 'Variant Title'
    ], []);

    const validateRow = useCallback((row: Record<string, string>, selectedMappings: FieldMapping[], _rowNumber: number): { isValid: boolean, errors: string[] } => {
        const errors: string[] = [];

        // Check required fields
        requiredFields.forEach((requiredField) => {
            const mapping = selectedMappings.find((m) =>
                (m.customName || m.aspireField) === requiredField && m.isSelected
            );

            if (mapping && mapping.csvColumn !== 'Select') {
                const value = row[mapping.csvColumn]?.trim();
                if (!value || value === '') {
                    errors.push(`Missing required field: ${requiredField}`);
                }
            } else {
                errors.push(`Required field not mapped: ${requiredField}`);
            }
        });

        return { isValid: errors.length === 0, errors };
    }, [requiredFields]);

    const defaultAspireFields = useMemo(() => [
        // Required fields
        { name: 'Collection ID', required: true },
        { name: 'Collection Name', required: true },
        { name: 'Product ID', required: true },
        { name: 'Product Handle', required: true },
        { name: 'Product Title', required: true },
        { name: 'Variant ID', required: true },
        { name: 'Variant SKU', required: true },
        { name: 'Variant Title', required: true },
        { name: 'Variant Price', required: true },
        // Optional fields
        { name: 'Collection Image', required: false },
        { name: 'Product Description', required: false },
        { name: 'Product Vendor', required: false },
        { name: 'Product Category', required: false },
        { name: 'Product Type', required: false },
        { name: 'Product Tags', required: false },
        { name: 'Product Status', required: false },
        { name: 'Product Image Src', required: false },
        { name: 'Variant Compare At Price', required: false },
        { name: 'Variant Inventory Quantity', required: false },
        { name: 'Variant Image Src', required: false },
        { name: 'Option1 Name', required: false },
        { name: 'Option1 Value', required: false },
        { name: 'Option2 Name', required: false },
        { name: 'Option2 Value', required: false },
        { name: 'Option3 Name', required: false },
        { name: 'Option3 Value', required: false },
        { name: 'Option4 Name', required: false },
        { name: 'Option4 Value', required: false },
    ], []);

    const validateCSV = (csvText: string): Record<string, string>[] => {
        const lines = csvText.split('\n');
        if (lines.length < 2) {
            throw new Error('CSV file must have at least a header row and one data row');
        }

      const headers = lines[0].split(',').map((header) => header.trim());

        const products: Record<string, string>[] = [];
        for (let i = 1; i < lines.length; i++) {
            if (lines[i].trim()) {
                const values = lines[i].split(',').map((value) => value.trim());
                const product: Record<string, string> = {};

                headers.forEach((header, index) => {
                    product[header] = values[index] || '';
                });

                products.push(product);
            }
        }

        return products;
    };

    const handleFileSelect = useCallback((file: File) => {
        setError(null);
        setSelectedFile(null);
        setCsvData([]);
        setCsvHeaders([]);

        if (!file.name.toLowerCase().endsWith('.csv')) {
            setError('Please select a valid CSV file');
            return;
        }

        if (file.size > 10 * 1024 * 1024) { // 10MB limit
            setError('File size must be less than 10MB');
            return;
        }

        const reader = new FileReader();
        reader.onload = (e) => {
            try {
                const csvText = e.target?.result as string;
                const products = validateCSV(csvText);
                const headers = csvText.split('\n')[0].split(',').map((header) => header.trim());

                setSelectedFile(file);
                setCsvData(products);
                setCsvHeaders(headers);

                // Initialize field mappings with smart matching
                const initialMappings: FieldMapping[] = defaultAspireFields.map((field, index) => {
                    let matchedColumn = 'Select';
                    let isSelected = false;

                    // Smart matching for common field names
                    switch (field.name.toLowerCase()) {
                        // Required fields - always selected
                        case 'collection id':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('collection') && h.toLowerCase().includes('id')) || 'Select';
                            isSelected = field.required;
                            break;
                        case 'collection name':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('collection') && h.toLowerCase().includes('name')) || 'Select';
                            isSelected = field.required;
                            break;
                        case 'product id':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('product') && h.toLowerCase().includes('id')) || 'Select';
                            isSelected = field.required;
                            break;
                        case 'product handle':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('handle') || h.toLowerCase().includes('product') && h.toLowerCase().includes('handle')) || 'Select';
                            isSelected = field.required;
                            break;
                        case 'product title':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('title') || h.toLowerCase().includes('product') && h.toLowerCase().includes('title')) || 'Select';
                            isSelected = field.required;
                            break;
                        case 'variant id':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('variant') && h.toLowerCase().includes('id')) || 'Select';
                            isSelected = field.required;
                            break;
                        case 'variant sku':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('sku') || h.toLowerCase().includes('variant') && h.toLowerCase().includes('sku')) || 'Select';
                            isSelected = field.required;
                            break;
                        case 'variant title':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('variant') && h.toLowerCase().includes('title')) || 'Select';
                            isSelected = field.required;
                            break;

                        // Optional fields
                        case 'collection image':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('collection') && h.toLowerCase().includes('image')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'product description':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('description') || h.toLowerCase().includes('body')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'product vendor':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('vendor')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'product category':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('category')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'product type':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('type')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'product tags':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('tags')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'product status':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('status')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'product image src':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('product') && h.toLowerCase().includes('image')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'variant price':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('variant') && h.toLowerCase().includes('price')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'variant compare at price':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('compare') && h.toLowerCase().includes('price')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'variant inventory quantity':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('inventory') || h.toLowerCase().includes('quantity')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'variant image src':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('variant') && h.toLowerCase().includes('image')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'option1 name':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('option1') && h.toLowerCase().includes('name')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'option1 value':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('option1') && h.toLowerCase().includes('value')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'option2 name':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('option2') && h.toLowerCase().includes('name')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'option2 value':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('option2') && h.toLowerCase().includes('value')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'option3 name':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('option3') && h.toLowerCase().includes('name')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'option3 value':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('option3') && h.toLowerCase().includes('value')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'option4 name':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('option4') && h.toLowerCase().includes('name')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        case 'option4 value':
                            matchedColumn = headers.find((h) => h.toLowerCase().includes('option4') && h.toLowerCase().includes('value')) || 'Select';
                            isSelected = matchedColumn !== 'Select';
                            break;
                        default:
                            matchedColumn = 'Select';
                            isSelected = false;
                    }

                    return {
                        id: `field-${index}`,
                        aspireField: field.name,
                        csvColumn: matchedColumn,
                        isSelected,
                        isRequired: field.required,
                        customName: field.name === 'Custom Name' ? 'Custom Name' : undefined
                    };
                });
                setFieldMappings(initialMappings);
            } catch (err) {
                setError(err instanceof Error ? err.message : 'Invalid CSV format');
            }
        };
        reader.onerror = () => {
            setError('Error reading file');
        };
        reader.readAsText(file);
    }, [defaultAspireFields]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
    }, []);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);

        const files = e.dataTransfer.files;
        if (files.length > 0) {
            handleFileSelect(files[0]);
        }
    }, [handleFileSelect]);

    const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) {
            handleFileSelect(file);
        }
    }, [handleFileSelect]);

    const handleUploadClick = useCallback(() => {
        fileInputRef.current?.click();
    }, []);

    const handleRemoveFile = useCallback(() => {
        setSelectedFile(null);
        setCsvData([]);
        setCsvHeaders([]);
        setFieldMappings([]);
        setError(null);
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    }, []);

    const handleMappingToggle = useCallback((fieldId: string, checked: boolean) => {
        setFieldMappings((prev) =>
            prev.map((mapping) =>
                (mapping.id === fieldId
                    ? { ...mapping, isSelected: checked }
                    : mapping)
            )
        );
    }, []);

    const handleColumnMapping = useCallback((fieldId: string, column: string) => {
        setFieldMappings((prev) =>
            prev.map((mapping) =>
                (mapping.id === fieldId
                    ? { ...mapping, csvColumn: column }
                    : mapping)
            )
        );
    }, []);

    const handleCustomNameChange = useCallback((fieldId: string, name: string) => {
        setFieldMappings((prev) =>
            prev.map((mapping) =>
                (mapping.id === fieldId
                    ? { ...mapping, customName: name }
                    : mapping)
            )
        );
    }, []);

    // const handleAddField = useCallback(() => {
    //     const newField: FieldMapping = {
    //         id: `field-${Date.now()}`,
    //         aspireField: 'Custom Field',
    //         csvColumn: 'Select',
    //         isSelected: true,
    //         customName: 'Custom Field'
    //     };
    //     setFieldMappings((prev) => [...prev, newField]);
    // }, []);

    const onUpload = async () => {
        if (!selectedFile) {
            setError('Please select a file to upload');
            return;
        }

        // Move to mapping step
        setCurrentStep('mapping');
    };

    const handleMappingNext = () => {
        // Validate required fields are mapped
        const selectedMappings = fieldMappings.filter((mapping) => mapping.isSelected);

        const missingRequiredFields = requiredFields.filter((requiredField) =>
            !selectedMappings.some((mapping) =>
                (mapping.customName || mapping.aspireField) === requiredField &&
                mapping.csvColumn !== 'Select'
            )
        );

        if (missingRequiredFields.length > 0) {
            setError(`Please map the following required fields: ${missingRequiredFields.join(', ')}`);
            return;
        }

        // Clear any previous errors and move to preview step
        setError(null);
        setCurrentStep('preview');
    };

    const handleStartImport = async () => {
        setCurrentStep('processing');
        setProcessingProgress(0);
        setError(null);

        try {
            const selectedMappings = fieldMappings.filter((mapping) => mapping.isSelected);

            // Validate and separate records
            const validProductRecords: ProductData[] = [];
            const invalidRecordsList: { row: Record<string, string>, errors: string[], rowNumber: number }[] = [];

            setProcessingProgress(10);

            csvData.forEach((row, index) => {
                const validation = validateRow(row, selectedMappings, index + 2); // +2 because first row is headers and we want 1-based indexing

                if (validation.isValid) {
                    // Process valid record
                    const mappedProduct = processRowToProduct(row, selectedMappings);
                    validProductRecords.push(mappedProduct);
                } else {
                    // Store invalid record with errors
                    invalidRecordsList.push({
                        row,
                        errors: validation.errors,
                        rowNumber: index + 2
                    });
                }
            });

            setValidRecords(validProductRecords);
            setInvalidRecords(invalidRecordsList);
            setProcessingProgress(50);

            // Only import valid records if there are any
            let importedCount = 0;
            if (validProductRecords.length > 0) {
                setProcessingProgress(75);

                // Call CSV import mutation with valid records only
                const result = await handleCSVImportCollections({
                    variables: {
                        records: validProductRecords
                    }
                });

                if (result.data?.handleCSVImportCollections) {
                    importedCount = validProductRecords.length;
                } else {
                    throw new Error('CSV import failed: Backend returned false');
                }
            }

            setProcessingProgress(100);

            // Set import results
            setImportResults({
                valid: validProductRecords.length,
                invalid: invalidRecordsList.length,
                imported: importedCount
            });

            // Move to results step
            setCurrentStep('results');
        } catch (err) {
            const errorMessage = csvImportError?.message || (err as Error).message || 'Import failed. Please try again.';
            setError(`Import failed: ${errorMessage}`);
            setCurrentStep('preview'); // Go back to preview step on error
        }
    };

    const handleBackToUpload = () => {
        setCurrentStep('upload');
    };

    const handleBackToMapping = () => {
        setCurrentStep('mapping');
    };

    const formatFileSize = (bytes: number): string => {
        if (bytes === 0) return '0 Bytes';
        const k = 1024;
        const sizes = ['Bytes', 'KB', 'MB', 'GB'];
        const i = Math.floor(Math.log(bytes) / Math.log(k));
        return `${parseFloat((bytes / (k ** i)).toFixed(2))} ${sizes[i]}`;
    };

    const renderUploadStep = () => (
      <>
        <DialogHeader className="flex flex-row items-center justify-between p-4 space-y-0">
          <p className="text-primary text-base font-medium m-0 flex items-center">Import Products from CSV </p>
        </DialogHeader>
        <div className="border-b" />
        <div className="flex flex-col gap-6 p-4">
          {error && (<ShadCnAlert title="Error" description={error} type="error" />)}

          <input
            ref={fileInputRef}
            type="file"
            accept=".csv"
            onChange={handleFileInputChange}
            className="hidden"
          />

          {!selectedFile ? (
            <div
              className={`py-8 px-6 border border-dashed rounded-xl cursor-pointer transition-all duration-200 ${
                            isDragOver
                                ? 'border-primary bg-primary/5'
                                : 'border-grey-3 hover:border-grey-4'
                        }`}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={handleUploadClick}
            >
              <div className="flex flex-col gap-3 items-center">
                <FontAwesomeIcon
                  icon={faFileArrowUp}
                  className="text-3xl text-grey-4"
                />
                <div className="text-center">
                  <span className="text-grey-5 text-sm font-medium">
                    {isDragOver ? 'Drop your CSV file here' : (
                      <>
                        Drag & drop a file or{' '}
                        <span className="text-primary underline cursor-pointer">
                          click to browse
                        </span>
                      </>
                    )}
                  </span>
                  <div className="text-grey-4 text-xs mt-2">
                    File Format: CSV
                  </div>
                </div>
              </div>
            </div>
                ) : (
                  <div className="py-8 px-6 border border-dashed border-grey-3 rounded-xl">
                    <div className="flex items-center justify-center">
                      <div className="flex flex-col items-center gap-2">
                        <PaperClipOutlined className="text-grey-4 text-xl" />
                        <div className="flex items-center gap-2">
                          <span className="text-grey-6 text-sm">{selectedFile.name}</span>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={handleRemoveFile}
                            className="h-4 w-4 p-0 hover:bg-grey-2"
                          >
                            <CloseOutlined className="text-xs" />
                          </Button>
                        </div>
                        <span className="text-grey-4 text-xs">{formatFileSize(selectedFile.size)}</span>
                      </div>
                    </div>
                  </div>
                )}

          <div>
            <ul className="list-disc pl-5 text-grey-6 text-sm font-normal flex flex-col gap-1 mb-0">
              <li><a href="https://storage.googleapis.com/aspirex-dev-static.aspireiq-dev.com/app/public/csv_collection_sample.csv" className="text-primary hover:text-grey-5 underline" target="_blank" rel="noopener noreferrer" download="sample-csv-template.csv">Download Sample CSV</a></li>
              <li><a href="https://help.aspireiq.com/en/articles/12134071-importing-products-with-a-csv-file" className="text-primary hover:text-grey-5 underline" target="_blank" rel="noopener noreferrer">Learn more about CSV imports</a></li>
            </ul>
          </div>
          <div>
            <Accordion type="single" collapsible className="w-full">
              <AccordionItem value="payouts" className="border-none">
                <AccordionTrigger className="py-2 hover:no-underline">
                  Instructions
                </AccordionTrigger>
                <AccordionContent>
                  <ul className="list-disc pl-5 text-primary text-sm font-normal flex flex-col gap-1 mb-0">
                    <li>
                      The CSV file must include the following required headers. Refer the sample CSV:
                      <br />
                      <span className="text-xs bg-grey-1 px-2 py-1 rounded mt-1 inline-block">
                        {requiredFieldNames}
                      </span>
                    </li>
                    <li>File size must be less than 10MB</li>
                    <li>Only CSV files are supported</li>
                    <li>Each product can have multiple variants (rows with same product handle, product title etc)</li>
                  </ul>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </div>
        </div>
        <div className="border-b" />
        <DialogFooter className="p-4">
          <Button key="cancel" variant="outline" type="button" onClick={handleCloseModal}>Cancel</Button>
          <Button
            variant="primary"
            type="submit"
            onClick={onUpload}
            disabled={!selectedFile || isUploading}
          >
            <span className="mr-2">
              {isUploading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        ) : (
                          <UploadOutlined />
                        )}
            </span>
            {isUploading ? 'Uploading...' : 'Upload'}
          </Button>
        </DialogFooter>
      </>
    );

    // Check if required fields can be mapped from CSV headers
    const canMapRequiredFields = useMemo(() => {
        if (!csvHeaders || csvHeaders.length === 0) return false;

        const unmappableFields = requiredFields.filter((requiredField) => {
            // Check if any CSV header can potentially match this required field
            const canMatch = csvHeaders.some((header) => {
                const headerLower = header.toLowerCase();
                const fieldLower = requiredField.toLowerCase();

                // Check for exact matches or partial matches for common patterns
                return headerLower.includes(fieldLower.replace(/\s+/g, '')) ||
                       headerLower.includes(fieldLower.replace(/\s+/g, '_')) ||
                       (fieldLower.includes('collection') && headerLower.includes('collection')) ||
                       (fieldLower.includes('product') && headerLower.includes('product')) ||
                       (fieldLower.includes('variant') && headerLower.includes('variant')) ||
                       (fieldLower.includes('id') && headerLower.includes('id')) ||
                       (fieldLower.includes('name') && headerLower.includes('name')) ||
                       (fieldLower.includes('title') && headerLower.includes('title')) ||
                       (fieldLower.includes('handle') && headerLower.includes('handle')) ||
                       (fieldLower.includes('sku') && headerLower.includes('sku'));
            });
            return !canMatch;
        });

        return unmappableFields.length === 0;
    }, [csvHeaders, requiredFields]);

    // Check if all required fields are actually mapped (selected and have valid column mapping)
    const areAllRequiredFieldsMapped = useMemo(() => {
        const selectedMappings = fieldMappings.filter((mapping) => mapping.isSelected);

        // Get all required field mappings
        const requiredMappings = selectedMappings.filter((mapping) => mapping.isRequired);

        // Check if all required fields are properly mapped
        return requiredMappings.every((mapping) =>
            mapping.csvColumn !== 'Select' && mapping.csvColumn !== ''
        );
    }, [fieldMappings]);

    // Get available CSV headers for a specific field mapping (excluding already used ones)
    const getAvailableHeaders = useCallback((currentMappingId: string) => {
        const usedColumns = fieldMappings
            .filter((mapping) => mapping.id !== currentMappingId && mapping.csvColumn !== 'Select')
            .map((mapping) => mapping.csvColumn);

        return csvHeaders.filter((header) => !usedColumns.includes(header));
    }, [fieldMappings, csvHeaders]);

    // Get required field names dynamically from defaultAspireFields
    const requiredFieldNames = useMemo(() => {
        return defaultAspireFields
            .filter((field) => field.required)
            .map((field) => field.name)
            .join(', ');
    }, [defaultAspireFields]);

    const renderMappingStep = () => (
      <>
        <DialogHeader className="flex flex-row items-center justify-between p-4 space-y-0">
          <p className="text-primary text-base font-medium m-0 flex items-center">
            CSV Field Mapping
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span className="cursor-pointer text-grey-4 ml-2">
                    <QuestionCircleFilled width={16} height={16} className="text-grey-4" />
                  </span>
                </TooltipTrigger>
                <TooltipContent>
                  <p>Match the columns from your CSV file to Aspire's required fields.</p>
                  <p>This ensures your data is correctly imported and mapped to the right properties inside Aspire.</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </p>
        </DialogHeader>
        <div className="border-b" />
        <div className="flex flex-col gap-2 p-4 max-h-[60vh] overflow-y-auto">
          {!canMapRequiredFields && (
            <ShadCnAlert
              title="Invalid CSV File"
              description="This CSV file is missing required header columns and cannot be mapped to the required fields. Please refer to the sample CSV and upload a valid file with all required headers."
              type="error"
            />
          )}

          {error && (<ShadCnAlert title="Error" description={error} type="error" />)}

          <div className="grid grid-cols-3 gap-4 items-center text-sm font-medium text-grey-6 pb-2 border-b border-grey-2">
            <div className="flex items-center gap-2">
              <Checkbox disabled checked />
              <span>Aspire's Heading</span>
            </div>
            <div className="flex justify-center">
              <span>→</span>
            </div>
            <div>
              <span>Column Heading</span>
            </div>
          </div>

          {fieldMappings.map((mapping) => (
            <div key={mapping.id} className="grid grid-cols-3 gap-4 items-center px-3 py-2 bg-grey-1 rounded-xl">
              <div className="flex items-center gap-2">
                <Checkbox
                  checked={mapping.isSelected}
                  onCheckedChange={(checked) => handleMappingToggle(mapping.id, checked as boolean)}
                  disabled={mapping.isRequired}
                />
                <div className="flex items-center gap-2">
                  <span className="text-sm text-grey-6 whitespace-nowrap">
                    {mapping.customName || mapping.aspireField}
                  </span>
                  {mapping.isRequired && (
                    <span className="px-2 py-1 bg-blue-100 text-blue-600 text-xs rounded-full flex-shrink-0">
                      Required
                    </span>
                                )}
                </div>
                {mapping.aspireField === 'Custom Name' && (
                <Input
                  value={mapping.customName || ''}
                  onChange={(e) => handleCustomNameChange(mapping.id, e.target.value)}
                  className="w-32 h-6 text-xs"
                  placeholder="Custom Name"
                />
                            )}
              </div>
              <div className="flex justify-center">
                <span className="text-grey-4">→</span>
              </div>
              <div>
                <Select
                  value={mapping.csvColumn}
                  onValueChange={(value) => handleColumnMapping(mapping.id, value)}
                >
                  <SelectTrigger className="w-full">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Select">Select</SelectItem>
                    {getAvailableHeaders(mapping.id).map((header) => (
                      <SelectItem key={header} value={header}>
                        {header}
                      </SelectItem>
                                    ))}
                    {/* Show currently selected option even if it would be filtered out */}
                    {mapping.csvColumn !== 'Select' && !getAvailableHeaders(mapping.id).includes(mapping.csvColumn) && (
                      <SelectItem key={mapping.csvColumn} value={mapping.csvColumn}>
                        {mapping.csvColumn}
                      </SelectItem>
                    )}
                  </SelectContent>
                </Select>
              </div>
            </div>
                ))}

          {/* <Button
            variant="outline"
            size="sm"
            onClick={handleAddField}
            className="w-fit mt-2"
          >
            <PlusOutlined className="mr-2" />
            Add Field
          </Button> */}
        </div>
        <div className="border-b" />
        <DialogFooter className="!p-4 !flex !flex-row !justify-between !items-center !space-x-0">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={handleBackToUpload}
              className="flex items-center gap-2 underline"
            >
              <ArrowLeftOutlined />
              Upload
            </Button>
          </div>
          <div className="flex gap-2 items-center">
            <Button
              variant="outline"
              type="button"
              onClick={handleCloseModal}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              onClick={handleMappingNext}
              disabled={!canMapRequiredFields || !areAllRequiredFieldsMapped}
              className="flex items-center gap-2"
            >
              Next
              <ArrowRightOutlined />
            </Button>
          </div>
        </DialogFooter>
      </>
    );

    const renderPreviewStep = () => {
        const selectedMappings = fieldMappings.filter((mapping) => mapping.isSelected);
        const sampleProduct = csvData[0] || {};

        return (
          <>
            <DialogHeader className="flex flex-row items-center justify-between p-4 space-y-0">
              <p className="text-primary text-base font-medium m-0 flex items-center">
                Preview
              </p>
            </DialogHeader>
            <div className="border-b" />
            <div className="flex flex-col gap-6 p-6 max-h-[60vh] overflow-y-auto">
              {/* Import Summary */}
              <div className="bg-grey-2 p-3 rounded-lg flex items-center gap-2">
                <CircleInfoIcon className="text-primary" />
                <span className="text-sm text-grey-6">{csvData.length} Products will be imported</span>
              </div>

              {/* Options */}
              {/* <div className="flex items-center gap-2">
                <Checkbox
                  checked={skipDuplicates}
                  onCheckedChange={(checked) => setSkipDuplicates(checked as boolean)}
                />
                <span className="text-sm text-grey-6">Skip duplicate products</span>
                <CircleInfoIcon className="text-grey-4 text-xs" />
              </div> */}

              {/* Product Data Preview */}
              <div className="rounded-lg p-4 bg-grey-1">
                {/* <h3 className="text-sm font-medium text-grey-6 mb-4">Sample Product Data</h3> */}
                <div className="space-y-4">
                  {selectedMappings.map((mapping) => {
                                const fieldName = mapping.customName || mapping.aspireField;
                                const fieldValue = sampleProduct[mapping.csvColumn] || 'N/A';
                                return (
                                  <div key={mapping.id} className="border-b border-grey-1 py-4 last:border-b-0">
                                    <div className="grid grid-cols-2 gap-4">
                                      <span className="text-sm font-semibold text-grey-6">{fieldName}</span>
                                      <span className="text-sm text-grey-6 break-words overflow-wrap-anywhere whitespace-pre-line">
                                        {fieldValue.replace(/\$/g, '\n')}
                                      </span>
                                    </div>
                                  </div>
                                );
                            })}
                </div>
              </div>
            </div>
            <div className="border-b" />
            <DialogFooter className="!p-4 !flex !flex-row !justify-between !items-center !space-x-0">
              <div className="flex items-center">
                <Button
                  variant="ghost"
                  onClick={handleBackToMapping}
                  className="flex items-center gap-2 underline"
                >
                  <ArrowLeftOutlined />
                  Back
                </Button>
              </div>
              <div className="flex gap-2 items-center">
                <Button
                  variant="outline"
                  type="button"
                  onClick={handleCloseModal}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  onClick={handleStartImport}
                  disabled={isUploading || isImportingCSVCollections}
                  className="flex items-center gap-2"
                >
                  {(isUploading || isImportingCSVCollections) ? 'Processing...' : 'Start Import'}
                  <UploadOutlined />
                </Button>
              </div>
            </DialogFooter>
          </>
        );
    };

    const renderProcessingStep = () => (
      <>
        <DialogHeader className="flex flex-row items-center justify-between p-4 space-y-0">
          <p className="text-primary text-base font-medium m-0 flex items-center">
            Processing CSV Import
          </p>
        </DialogHeader>
        <div className="border-b" />
        <div className="flex flex-col gap-6 p-6 items-center justify-center min-h-[300px]">
          <div className="w-10 h-10 border-4 border-primary border-t-transparent rounded-full animate-spin" />
          <div className="text-center">
            <p className="text-lg font-medium text-grey-6 mb-2">Processing your CSV file...</p>
            <p className="text-sm text-grey-5">Validating data and importing records</p>
          </div>
          <div className="w-full max-w-md">
            <div className="flex justify-between text-sm text-grey-5 mb-2">
              <span>Progress</span>
              <span>{processingProgress}%</span>
            </div>
            <div className="w-full bg-grey-2 rounded-full h-2">
              <div
                className="bg-primary h-2 rounded-full transition-all duration-300 ease-in-out"
                style={{ width: `${processingProgress}%` }}
              />
            </div>
          </div>
        </div>
      </>
    );

    const renderResultsStep = () => (
      <>
        <DialogHeader className="flex flex-row items-center justify-between p-4 space-y-0">
          <p className="text-primary text-base font-medium m-0 flex items-center">
            Import Results
          </p>
        </DialogHeader>
        <div className="border-b" />
        <div className="flex flex-col gap-6 p-6 max-h-[60vh] overflow-y-auto">
          {error && (<ShadCnAlert title="Error" description={error} type="error" />)}

          {importResults && (
            <>
              {/* Success Summary */}
              <div className="text-center">
                <div className="w-16 h-16 bg-grey-1 rounded-full flex items-center justify-center mx-auto mb-4">
                  <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <h3 className="text-lg font-semibold text-grey-6 mb-2">
                  {importResults.invalid === 0 ? 'Import Completed Successfully!' : 'Import Completed with Issues'}
                </h3>
                <p className="text-sm text-grey-5">
                  {importResults.invalid === 0
                    ? `All ${importResults.imported} records were imported successfully.`
                    : `${importResults.imported} records imported successfully, ${importResults.invalid} records had issues.`}
                </p>
              </div>

              {/* Statistics */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 rounded-2xl text-center">
                  <div className="text-2xl font-bold">{importResults.imported}</div>
                  <div className="text-sm">Imported</div>
                </div>
                <div className="p-4 rounded-2xl text-center">
                  <div className="text-2xl font-bold text-red-600">{importResults.invalid}</div>
                  <div className="text-sm text-red-600">Invalid Records</div>
                </div>
              </div>

              {/* Invalid Records Details */}
              {importResults.invalid > 0 && (
                <div className="rounded-2xl bg-grey-1 p-4">
                  <h4 className="font-medium mb-3">Invalid Records ({importResults.invalid})</h4>
                  <div className="max-h-32 overflow-y-auto">
                    {invalidRecords.map((record, index) => (
                      <div key={index} className="mb-3 p-3 bg-white rounded-2xl border">
                        <div className="text-sm font-medium mb-2">
                          Row {record.rowNumber}
                        </div>
                        <div className="text-xs text-red-600">
                          {record.errors.map((error, errorIndex) => (
                            <div key={errorIndex}>• {error}</div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
        <div className="border-b" />
        <DialogFooter className="p-4">
          <Button
            variant="outline"
            type="button"
            onClick={handleCloseModal}
          >
            Close
          </Button>
        </DialogFooter>
      </>
    );

    return (
      <Dialog open={isProductUploadModalOpen} onOpenChange={(open) => !open && handleCloseModal()}>
        <DialogContent className={`border-r-4 rounded-xl bg-grey-0 p-0 gap-0 ${
          currentStep === 'mapping' ? 'w-[700px] max-w-[700px] max-h-[80vh]' :
          currentStep === 'preview' ? 'w-[700px] max-w-[700px] max-h-[80vh]' :
          currentStep === 'results' ? 'w-[550px] max-w-[550px] max-h-[80vh]' :
          currentStep === 'processing' ? 'w-[500px] max-w-[500px]' :
          'w-[506px] max-w-[506px]'
        }`}
        >
          {(() => {
            switch (currentStep) {
              case 'upload': return renderUploadStep();
              case 'mapping': return renderMappingStep();
              case 'preview': return renderPreviewStep();
              case 'processing': return renderProcessingStep();
              case 'results': return renderResultsStep();
              default: return renderUploadStep();
            }
          })()}
        </DialogContent>
      </Dialog>
    );
};
